<template>
    <b-modal
        id="user-search-modal"
        ref="modal"
        :title="$t('사용자 검색')"
        size="xl"
        hide-footer
        modal-class="user-search-modal"
        @hidden="resetModal"
        @hide="resetModal"
    >
        <b-container fluid>
            <b-card no-body bg-variant="white" class="mb-3">
                <template #header>
                    <div class="d-flex align-items-center justify-content-between">
                        <h5 class="mb-0">{{ $t('사용자 검색하기') }}</h5>
                        <div v-show="listLoading">
                            <div class="d-flex align-items-center">
                                <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                                <p class="d-inline-block mb-0">{{ $t('조회중') }}...</p>
                            </div>
                        </div>
                        <div v-show="!listLoading">
                            <b-btn
                                variant="primary"
                                :disabled="!isAbleToSearch || listLoading"
                                @click="searchData"
                            >
                                {{ $t('검색하기') }}
                            </b-btn>
                        </div>
                    </div>
                </template>

                <div class="p-3">
                    <b-row>
                        <b-col cols="12" md="4">
                            <b-form-group
                                :label="$t('이메일')"
                                label-for="user-search-email"
                                class="mb-0"
                            >
                                <b-form-input id="user-search-email"
                                              v-model="searchFormValue.e_mail"
                                              :disabled="listLoading"
                                              :placeholder="$t('이메일로 검색')"
                                              trim
                                              @keypress.enter="searchData"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4">
                            <b-form-group
                                :label="$t('사용자명')"
                                label-for="user-search-name"
                                class="mb-0"
                            >
                                <b-form-input id="user-search-name"
                                              v-model="searchFormValue.user_name"
                                              :disabled="listLoading"
                                              :placeholder="$t('사용자명으로 검색')"
                                              trim
                                              @keypress.enter="searchData"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4">
                            <b-form-group
                                :label="$t('소속 기업명')"
                                label-for="user-search-company"
                                class="mb-0"
                            >
                                <b-form-input id="user-search-company"
                                              v-model="searchFormValue.company_name"
                                              :disabled="listLoading"
                                              :placeholder="$t('기업명으로 검색')"
                                              trim
                                              @keypress.enter="searchData"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>
            </b-card>

            <b-card
                :class="[{ 'shadow-sm': searchResults || (!listLoading && searchResults) }, 'results-card']"
                :bg-variant="!searchResults ? 'light' : 'white'"
                no-body
            >
                <template #header>
                    <div class="d-flex align-items-center justify-content-between" style="height: 36px">
                        <h6 class="mb-0">{{ $t('사용자 목록') }}</h6>
                        <div class="d-flex align-items-center">
                            <b-button variant="outline-secondary" class="mr-2"
                                      @click="resetSelected"
                            >
                                {{ $t('선택 초기화') }}
                            </b-button>
                            <b-button variant="outline-primary" @click="selectData">
                                {{ $t('선택한 사용자 추가') }}
                            </b-button>
                        </div>
                    </div>
                </template>
                <div class="results-table-container">
                    <b-table
                        fixed responsive
                        :items="searchResults"
                        :fields="tableColumns"
                        class="mb-0"
                        :empty-html="getEmptyTableHTML"
                        show-empty
                        :per-page="pagination.perPage"
                        :current-page="pagination.currentPage"
                        :busy="listLoading"
                    >
                        <template #head(selected)>
                            <b-form-checkbox
                                v-model="selectedAll"
                                :indeterminate="indeterminate"
                                :disabled="!(searchResults && searchResults.length > 0)"
                                aria-label="Select all rows"
                                @change="toggleSelectAll"
                            ></b-form-checkbox>
                        </template>
                        <template #cell(selected)="{ item }">
                            <b-form-checkbox
                                v-if="searchResults && searchResults.length > 0"
                                v-model="mapSelected[item.user_id].selected"
                                aria-label="Select row"
                                @change="selectRow(item)"
                            ></b-form-checkbox>
                        </template>

                        <template #cell(e_mail)="row">
                            <span v-b-tooltip.hover="getTooltipTitle(row.value)">{{ row.value }}</span>
                        </template>
                        <template #cell(user_name)="row">
                            <span v-b-tooltip.hover="getTooltipTitle(row.value)">{{ row.value }}</span>
                        </template>
                        <template #cell(company_name)="row">
                            <span v-b-tooltip.hover="getTooltipTitle(row.value)">{{ row.value }}</span>
                        </template>
                    </b-table>
                    <div v-if="searchResults && searchResults.length > 0" class="pagination-container">
                        <b-pagination
                            v-model="pagination.currentPage"
                            :total-rows="pagination.totalRows"
                            :per-page="pagination.perPage"
                            align="fill"
                            size="sm"
                        ></b-pagination>
                    </div>
                </div>
            </b-card>
        </b-container>
    </b-modal>
</template>

<script>
import {
    getDefaultPagination,
    getEmptyTableHTML,
    onTooltipShow
} from '@shared/components/admin/pages/Management/common-data';

export default {
    name: 'UserSearchModal',
    props: {
        excludeCompanyId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            listLoading: false,
            searchResults: null,
            searchFormValue: {
                e_mail: null,
                user_name: null,
                company_name: null,
            },
            pagination: getDefaultPagination(),

            selectedAll: false,
            currentSelectedNum: 0,
            mapSelected: {},
        };
    },
    computed: {
        tableColumns() {
            return [
                { key: 'selected', label: '', thStyle: { width: '40px' }, thClass: 'text-center', tdClass: 'text-center' },
                { key: 'user_id', label: this.$t('유저 ID'), thStyle: { width: '90px' } },
                { key: 'e_mail', label: this.$t('이메일(로그인 ID)') },
                { key: 'user_name', label: this.$t('사용자명'), thStyle: { width: '120px' } },
                { key: 'company_name', label: this.$t('소속 기업'), thStyle: { width: '200px' } },
            ];
        },
        indeterminate() {
            return this.currentSelectedNum > 0 && this.currentSelectedNum < (this.searchResults?.length || 0);
        },
        isAbleToSearch() {
            return Object.values(this.searchFormValue).some(_value => !!_value);
        },
        getEmptyTableHTML() {
            return getEmptyTableHTML(this.$t('조회된 사용자가 없습니다.'));
        },
    },
    mounted() {
        this.$root.$on('bv::tooltip::show', this.onTooltipShow);
    },
    beforeDestroy() {
        this.$root.$off('bv::tooltip::show', this.onTooltipShow);
    },
    methods: {
        onTooltipShow,
        getTooltipTitle(data) {
            return data ? { title: data } : null;
        },
        async searchData() {
            try {
                if (!this.isAbleToSearch) {
                    this.$EventBus.$emit('toast-failure', this.$t('검색 오류'), this.$t('검색어를 입력해주세요.'));
                    return;
                }

                this.listLoading = true;
                this.searchResults = null;
                this.currentSelectedNum = 0;

                const { e_mail, user_name, company_name } = this.searchFormValue;

                const res = await this.$axios.get('/admin/users', {
                    params: { e_mail, user_name, company_name }
                });

                if (res.data == null || res.data.length === 0) {
                    this.$EventBus.$emit('toast-success', this.$t('검색 완료'), this.$t('사용자 검색 결과가 없습니다.'));
                    this.searchResults = [];
                    return;
                }

                this.searchResults = res.data.map((item) => {
                    const data = { ...item, selected: false };
                    if (this.mapSelected[item.user_id] == null) {
                        this.$set(this.mapSelected, item.user_id, data);
                    }
                    return data;
                });

                this.pagination.totalRows = this.searchResults.length;
            } catch (error) {
                console.error(error);
                this.$EventBus.$emit('toast-failure', this.$t('검색 오류'), this.$t('사용자 검색 중 오류가 발생했습니다.'));
            } finally {
                this.listLoading = false;
            }
        },
        selectRow(row) {
            if (row.selected) {
                this.currentSelectedNum += 1;
            } else {
                this.currentSelectedNum -= 1;
            }
        },
        toggleSelectAll(value) {
            if (!this.searchResults || this.searchResults.length <= 0) return;

            for (let i = 0; i < this.searchResults.length; i++) {
                const elem = this.searchResults[i];
                this.mapSelected[elem.user_id].selected = !!value;
            }

            let selectedCount = 0;
            Object.values(this.mapSelected).forEach((item) => {
                if (item.selected) {
                    selectedCount += 1;
                }
            });
            this.currentSelectedNum = selectedCount;
        },
        resetSelected() {
            this.selectedAll = false;
            this.currentSelectedNum = 0;
            Object.keys(this.mapSelected).forEach((key) => {
                this.mapSelected[key].selected = false;
            });
        },
        selectData() {
            const mapSelectedItem = {};
            Object.values(this.mapSelected).forEach((item) => {
                if (item.selected) {
                    mapSelectedItem[item.user_id] = item;
                }
            });
            if (Object.keys(mapSelectedItem).length === 0) {
                this.$EventBus.$emit('toast-failure', this.$t('사용자 선택'), this.$t('사용자를 선택해주세요.'));
                return;
            }
            this.$emit('on-select', mapSelectedItem);
        },
        resetModal() {
            this.listLoading = false;
            this.searchResults = null;
            this.searchFormValue = {
                e_mail: null,
                user_name: null,
                company_name: null,
            };
            this.pagination = getDefaultPagination();
            this.selectedAll = false;
            this.currentSelectedNum = 0;
            this.mapSelected = {};
        },
    }
};
</script>

<style lang="scss">
.user-search-modal {
    .modal-dialog {
        max-width: 900px;
        height: 90vh;
        display: flex;
        flex-direction: column;
    }

    .modal-content {
        height: 90vh;
        display: flex;
        flex-direction: column;
    }

    .modal-body {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        flex: 1;
        min-height: 0;
    }

    .container-fluid {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow: hidden;
    }

    .results-card {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;

        .card-header {
            flex-shrink: 0;
        }

        .card-body {
            flex: 1;
            min-height: 0;
            overflow: hidden;
            padding: 0;
        }
    }

    .results-table-container {
        display: flex;
        flex-direction: column;
        height: 100%;

        .table-responsive {
            flex: 1;
            overflow-y: auto;
            min-height: 0;
        }

        .pagination-container {
            flex-shrink: 0;
            border-top: 1px solid #dee2e6;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            padding: 8px 16px;
        }
    }
}
</style>
